import React, { useState, useEffect } from 'react';
import {
  Box, Select, Text, Tooltip, useDisclosure, Modal, ModalOverlay, ModalContent,
  ModalHeader, ModalBody, ModalFooter, ModalCloseButton, Button, Input,
  FormControl, FormLabel, useToast, Icon,
} from '@chakra-ui/react';
import { getIconByKey, getWorkCentreIconByName } from './iconMap';
import api from '../api';

// Resolve icon for a work centre: DB icon field first, then name-based fallback
function getWorkCentreIcon(name, iconKey) {
  if (iconKey) {
    const ic = getIconByKey(iconKey);
    if (ic) return ic;
  }
  const fallbackKey = getWorkCentreIconByName(name);
  if (fallbackKey) return getIconByKey(fallbackKey);
  return null;
}

// Short label for the dropdown (remove common prefixes for compactness)
function getShortLabel(name) {
  if (!name) return '';
  // In the dropdown we show full name; in the cell we show icon only
  return name;
}

export default function WorkCentreSelect({ value, workCentreId, onSelect }) {
  const [centres, setCentres] = useState([]);
  const [editing, setEditing] = useState(false);
  const newModal = useDisclosure();
  const [newName, setNewName] = useState('');
  const toast = useToast();

  useEffect(() => {
    api.get('/work-centres').then(({ data }) => setCentres(data)).catch(() => {});
  }, []);

  const handleChange = (e) => {
    const val = e.target.value;
    if (val === '__new__') {
      setEditing(false);
      setNewName('');
      newModal.onOpen();
      return;
    }
    if (val) {
      onSelect(parseInt(val));
    } else {
      onSelect(null);
    }
    setEditing(false);
  };

  const handleCreate = async () => {
    if (!newName.trim()) return;
    try {
      const { data } = await api.post('/work-centres', { name: newName.trim() });
      setCentres(prev => [...prev, data].sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0)));
      onSelect(data.id);
      setNewName('');
      newModal.onClose();
      toast({ title: `"${data.name}" created`, status: 'success', duration: 2000 });
    } catch (err) {
      toast({ title: err.response?.data?.error || 'Failed', status: 'error', duration: 2000 });
    }
  };

  const currentCentre = centres.find(c => c.id === workCentreId);
  const displayName = currentCentre?.name || value || '';
  const IconComponent = getWorkCentreIcon(displayName, currentCentre?.icon);

  return (
    <>
      {!editing ? (
        <Tooltip label={displayName || 'Select work centre...'} fontSize="xs" placement="top" hasArrow>
          <Box
            cursor="pointer"
            onClick={() => setEditing(true)}
            minH="20px"
            display="flex"
            alignItems="center"
            justifyContent="center"
            _hover={{ borderRadius: 'sm' }}
            px={1}
            py={0.5}
          >
            {IconComponent ? (
              <Icon as={IconComponent} boxSize={4} color={displayName ? 'gray.700' : 'gray.300'} />
            ) : (
              <Text fontSize="11px" color={displayName ? 'gray.800' : 'gray.400'} noOfLines={1}>
                {displayName || '—'}
              </Text>
            )}
          </Box>
        </Tooltip>
      ) : (
        <Select
          size="xs"
          fontSize="11px"
          value={workCentreId || ''}
          onChange={handleChange}
          onBlur={() => setTimeout(() => setEditing(false), 200)}
          autoFocus
          
          
          
          
          
          
        >
          <option value="">— Select —</option>
          {centres.map(c => (
            <option key={c.id} value={c.id}>{c.name}</option>
          ))}
          <option value="__new__">+ Add new...</option>
        </Select>
      )}

      <Modal isOpen={newModal.isOpen} onClose={newModal.onClose} size="xs">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader fontSize="sm">New Work Centre</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <FormControl>
              <FormLabel fontSize="xs">Name</FormLabel>
              <Input
                size="sm"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
                autoFocus
              />
            </FormControl>
          </ModalBody>
          <ModalFooter>
            <Button size="sm" variant="ghost" mr={2} onClick={newModal.onClose}>Cancel</Button>
            <Button size="sm" bg="brand.600"  _hover={{ bg: 'brand.700' }} onClick={handleCreate}>
              Create
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
}
